import React, { useEffect, useState, useRef } from 'react';
import { detectMobileDevice, getAppStoreUrl, setMobileBypass } from '../../utils/mobileDetection';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import './index.scss';

const fallbackLogo = require('../../assets/common/resources/SIKI_Beta.png');
const fallbackLoading = {
  icon: require('../../assets/common/resources/Sikibot.png'),
  alt: 'Loading',
};

const MobileLanding = () => {
  const [platform, setPlatform] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const contentRef = useRef(null);
  const activeSectionRef = useRef('home');
  const { getCurrentBranding, getLoadingConfig } = useAppTheme();
  const brandingDefaults = { logo: fallbackLogo, logoAlt: 'SIKI Logo' };
  const currentBranding = getCurrentBranding ? getCurrentBranding() : brandingDefaults;
  const landingBranding = {
    ...brandingDefaults,
    ...currentBranding,
  };
  const themeLoading = getLoadingConfig ? getLoadingConfig() : {};
  const loadingAssets = {
    ...fallbackLoading,
    ...themeLoading,
  };
  
  // Keep ref in sync with state
  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const { isAndroid, isIOS } = detectMobileDevice();
    
    if (isAndroid) {
      setPlatform('android');
    } else if (isIOS) {
      setPlatform('ios');
    } else {
      setPlatform('unknown');
    }

    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    // Add scroll listener for parallax effects
    const handleScroll = () => {
      if (contentRef.current) {
        const scrollTop = contentRef.current.scrollTop;
        const scrollHeight = contentRef.current.scrollHeight - contentRef.current.clientHeight;
        const progress = (scrollTop / scrollHeight) * 100;
        setScrollProgress(progress);
      }
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleAppStoreClick = () => {
    window.location.href = getAppStoreUrl('ios');
  };

  const handlePlayStoreClick = () => {
    window.location.href = getAppStoreUrl('android');
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const navigateToSection = (section) => {
    setActiveSection(section);
    setMenuOpen(false);
  };

  const handlePlaybarClick = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    if (percentage <= 25) {
      navigateToSection('home');
    } else if (percentage <= 50) {
      navigateToSection('about');
    } else if (percentage <= 75) {
      navigateToSection('how');
    } else {
      navigateToSection('features');
    }
  };

  const handlePlayheadDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX || (e.touches && e.touches[0].clientX);
    const sections = ['home', 'about', 'how', 'features'];
    const startSection = activeSection; // Use state directly, not ref
    const startSectionIndex = sections.indexOf(startSection);
    let targetSection = null;
    let isDraggingLocal = true;
    
    // Disable transitions immediately
    setIsDragging(true);
    
    const handleMouseMove = (event) => {
      if (!isDraggingLocal) return;
      
      const clientX = event.clientX || (event.touches && event.touches[0].clientX);
      const deltaX = clientX - startX;
      
      // Threshold to prevent accidental triggers
      if (Math.abs(deltaX) > 20) {
        if (deltaX < 0 && startSectionIndex > 0) {
          // Dragged left - go to previous section
          targetSection = sections[startSectionIndex - 1];
        } else if (deltaX > 0 && startSectionIndex < sections.length - 1) {
          // Dragged right - go to next section
          targetSection = sections[startSectionIndex + 1];
        }
      }
    };
    
    const handleMouseUp = () => {
      isDraggingLocal = false;
      
      // Clean up listeners immediately
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
      
      // Apply the section change if we have a target
      if (targetSection && targetSection !== startSection) {
        // Use a timeout to ensure the drag state is properly handled
        setTimeout(() => {
          setActiveSection(targetSection);
          activeSectionRef.current = targetSection;
          setMenuOpen(false);
        }, 0);
      }
      
      // Re-enable transitions after state update
      setTimeout(() => {
        setIsDragging(false);
      }, 100);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
    document.addEventListener('touchend', handleMouseUp);
  };

  const renderContent = () => {
    switch(activeSection) {
      case 'about':
        return (
          <div className="about-section animated-section">
            <h2>About SIKI</h2>
            <div className="about-content">
              <div className="intro-box slide-in-left">
                <h3>Making Streams Come True</h3>
                <p>
                  SIKI is redefining music and social media by creating a platform where artists, influencers, curators, and fans 
                  earn fair and transparent royalties for every music stream. Using blockchain-enabled scaling royalties, 
                  SIKI ensures creators are paid accurately and timely.
                </p>
              </div>

              <div className="problem-solution">
                <h3>The Problem We Solve</h3>
                <ul className="problems">
                  <li><span className="icon">⚠️</span> Delayed royalty payments</li>
                  <li><span className="icon">⚠️</span> Lack of transparency on earnings</li>
                  <li><span className="icon">⚠️</span> Music has never been paid during a live stream</li>
                </ul>
                
                <h3>Our Solution</h3>
                <ul className="solutions">
                  <li><span className="icon">✅</span> Real-time blockchain-powered payments</li>
                  <li><span className="icon">✅</span> Full transparency with on-chain tracking</li>
                  <li><span className="icon">✅</span> Music played through SIKI in livestream is paid</li>
                </ul>
              </div>
            </div>
          </div>
        );
      
      case 'how':
        return (
          <div className="how-section animated-section">
            <h2>How SIKI Works</h2>
            <div className="how-content">
              <div className="user-types">
                <div className="user-type">
                  <h3>🎵 Artists & Labels</h3>
                  <ul>
                    <li>Upload music and earn streaming royalties</li>
                    <li>Host live concerts and events</li>
                    <li>Earn from tips, auctions, and subscriptions</li>
                    <li>Create and sell NFTs</li>
                  </ul>
                </div>
                
                <div className="user-type">
                  <h3>📱 Influencers</h3>
                  <ul>
                    <li>Create social media content</li>
                    <li>Earn % from streams in user-generated content</li>
                    <li>Host livestreaming events</li>
                    <li>Build engaged communities</li>
                  </ul>
                </div>
                
                <div className="user-type">
                  <h3>🎧 Curators</h3>
                  <ul>
                    <li>Build customized playlists</li>
                    <li>Earn % from streaming royalties</li>
                    <li>Promote new artist discovery</li>
                    <li>Drive community engagement</li>
                  </ul>
                </div>
                
                <div className="user-type">
                  <h3>👥 Listeners</h3>
                  <ul>
                    <li>Explore exclusive music</li>
                    <li>Support favorite artists directly</li>
                    <li>Get exclusive access and purchases</li>
                    <li>Earn token rewards</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 'features':
        return (
          <div className="features-section animated-section">
            <h2>Platform Features</h2>
            <div className="features-content">
              <div className="feature-category">
                <h3>🎵 Streaming</h3>
                <ul>
                  <li>Adaptive music & video streaming</li>
                  <li>Offline content download</li>
                  <li>Only platform to earn royalties from livestreams</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>💰 SIKI Token</h3>
                <ul>
                  <li>Instant payments</li>
                  <li>Marketplace transactions</li>
                  <li>Governance voting rights</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>🔗 Blockchain</h3>
                <ul>
                  <li>NFT minting and trading</li>
                  <li>Transparent payment tracking</li>
                  <li>On-chain royalty distribution</li>
                </ul>
              </div>
              
              <div className="feature-category">
                <h3>🎮 Gamification</h3>
                <ul>
                  <li>Level up by sharing music or growing your own</li>
                  <li>Buy and sell collectibles that level up with more song streams</li>
                  <li>Earn royalties as users listen to your studio</li>
                </ul>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="hero-section animated-section">
            <div className="hero-headline-box">
              <span className="music-note left">♪</span>
              <h1 className="hero-headline">THE FUTURE OF MUSIC AND LIVE STREAMING</h1>
              <span className="music-note right">♪</span>
            </div>
            
            <div className="info-boxes">
              <div className="info-box fade-in-text">
                <p>The first music and live streaming platform that pays artists and curators in real time for every stream, view, tip, and purchase using blockchain based infrastructure.</p>
              </div>
              <div className="info-box fade-in-delayed">
                <p>Our ecosystem unlocks new monetization opportunities, closes the royalty gap, and brings full transparency to creators.</p>
              </div>
            </div>

            <div className="app-showcase parallax-container">
              <div className="showcase-title">Experience SIKI</div>
              <div className="showcase-images carousel-container">
                {(() => {
                  const hs = (landingBranding && landingBranding.homeScreens) || {};
                  const img1 = hs.app1 || require('../../assets/mobile-screenshots/screen1.jpg');
                  const img2 = hs.app2 || require('../../assets/mobile-screenshots/screen2.jpg');
                  const img3 = hs.app3 || require('../../assets/mobile-screenshots/screen3.jpg');
                  const img4 = hs.app4 || require('../../assets/mobile-screenshots/screen4.jpg');
                  return (
                    <>
                      <div className="app-screen screen-1 float-animation">
                        <img src={img1} alt="App screenshot 1" className="screenshot" />
                        <div className="screen-overlay"></div>
                      </div>
                      <div className="app-screen screen-2 float-animation-delayed">
                        <img src={img2} alt="App screenshot 2" className="screenshot" />
                        <div className="screen-overlay"></div>
                      </div>
                      <div className="app-screen screen-3 float-animation">
                        <img src={img3} alt="App screenshot 3" className="screenshot" />
                        <div className="screen-overlay"></div>
                      </div>
                      <div className="app-screen screen-4 float-animation-delayed">
                        <img src={img4} alt="App screenshot 4" className="screenshot" />
                        <div className="screen-overlay"></div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <p className="showcase-note">
                Stream music, support artists, and earn rewards - all in one app
              </p>
            </div>

            <div className="features stagger-animation">
              <div className="feature card-3d">
                <div className="feature-icon pulse-animation">
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <h3>Real-Time Payments</h3>
                <p>Artists get paid instantly for every stream and interaction</p>
              </div>
              <div className="feature card-3d">
                <div className="feature-icon pulse-animation">
                  <i className="fas fa-broadcast-tower"></i>
                </div>
                <h3>Live Streaming</h3>
                <p>The ONLY platform paying creators for every stream viewer</p>
              </div>
              <div className="feature card-3d">
                <div className="feature-icon pulse-animation">
                  <i className="fas fa-link"></i>
                </div>
                <h3>Blockchain Powered</h3>
                <p>Transparent, secure transactions with blockchain technology</p>
              </div>
              <div className="feature card-3d">
                <div className="feature-icon pulse-animation">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Full Transparency</h3>
                <p>Track earnings and analytics in real-time</p>
              </div>
            </div>

            <div className="download-section fade-in-up">
              <h2>Get Started with SIKI</h2>
              <p>Join the revolution in music streaming and artist compensation</p>
              
              <div className="app-buttons">
                {(platform === 'ios' || platform === 'unknown') && (
                  <button 
                    className="app-store-btn ripple-effect" 
                    onClick={handleAppStoreClick}
                    aria-label="Download on App Store"
                  >
                    <div className="btn-content">
                      <i className="fab fa-apple"></i>
                      <div className="btn-text">
                        <span className="small-text">Download on the</span>
                        <span className="main-text">App Store</span>
                      </div>
                    </div>
                  </button>
                )}
                
                {(platform === 'android' || platform === 'unknown') && (
                  <button 
                    className="play-store-btn ripple-effect" 
                    onClick={handlePlayStoreClick}
                    aria-label="Get it on Google Play"
                  >
                    <div className="btn-content">
                      <i className="fab fa-google-play"></i>
                      <div className="btn-text">
                        <span className="small-text">Get it on</span>
                        <span className="main-text">Google Play</span>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className={`mobile-landing ${isLoading ? 'loading' : 'loaded'}`}>
      {isLoading && (
        <div className="loading-screen">
          <div className="loader">
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <div className="loader-ring"></div>
            <img src={loadingAssets.icon} alt={loadingAssets.alt || 'Loading'} className="loader-logo" />
          </div>
        </div>
      )}
      <div className="progress-bar" style={{ width: `${scrollProgress}%` }}></div>
      <div className="mobile-landing__container">
        <div className={`mobile-landing__header section-${activeSection} ${isDragging ? 'dragging' : ''}`}>
          <img src={landingBranding.logo} alt={landingBranding.logoAlt || 'SIKI'} className="logo" />
          <button className="hamburger-menu" onClick={toggleMenu}>
            <span className={menuOpen ? 'open' : ''}></span>
            <span className={menuOpen ? 'open' : ''}></span>
            <span className={menuOpen ? 'open' : ''}></span>
          </button>
          <div className="playbar-container" onClick={handlePlaybarClick}>
            <div 
              className="playhead" 
              onMouseDown={handlePlayheadDrag}
              onTouchStart={handlePlayheadDrag}
            ></div>
          </div>
        </div>

        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
          <nav>
            <button onClick={() => navigateToSection('home')} className={activeSection === 'home' ? 'active' : ''}>
              Home
            </button>
            <button onClick={() => navigateToSection('about')} className={activeSection === 'about' ? 'active' : ''}>
              About SIKI
            </button>
            <button onClick={() => navigateToSection('how')} className={activeSection === 'how' ? 'active' : ''}>
              How It Works
            </button>
            <button onClick={() => navigateToSection('features')} className={activeSection === 'features' ? 'active' : ''}>
              Features
            </button>
            <a href="mailto:support@siki.io">Contact</a>
          </nav>
        </div>

        <div className="mobile-landing__content" ref={contentRef}>
          {renderContent()}
        </div>

        <div className="mobile-landing__footer">
          <p className="tagline">Fueling Rhythm, Redefining Royalties</p>
          <div className="footer-links">
            <a href="/PrivacyPolicy" className="footer-link">Privacy Policy</a>
            <span className="separator">•</span>
            <a href="/TermsAndCondition" className="footer-link">Terms of Service</a>
          </div>
          <p className="copyright">© 2025 SIKI. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default MobileLanding;
